import React from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, Monitor, Printer, RefreshCw, Smartphone } from 'lucide-react';

import { FileService, type HtmlArtifactPreview } from '@/shared/storage/FileService';
import type { FileAttachment } from '@/shared/types/projectData';
import { cn } from '@/ui/theme/ThemeContext';
import { requestViewerFullscreen } from './htmlArtifactFullscreen';
import { createPrintPreviewDocument } from './htmlArtifactPrintPreview';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

type PreviewWidthMode = 'desktop' | 'mobile' | 'print' | 'custom';

interface SecureHtmlArtifactPreviewProps {
  file: FileAttachment;
  className?: string;
  variant?: 'inline' | 'modal' | 'thumbnail';
}

const frameWidth = (mode: PreviewWidthMode, customWidth: number): string => {
  if (mode === 'mobile') return '390px';
  if (mode === 'print') return '794px';
  if (mode === 'custom') return `${customWidth}px`;
  return '100%';
};

const SecureHtmlArtifactViewer: React.FC<SecureHtmlArtifactPreviewProps> = ({
  file,
  className,
  variant = 'inline',
}) => {
  const { t } = useSettings();
  const shellRef = React.useRef<HTMLDivElement>(null);
  const [preview, setPreview] = React.useState<HtmlArtifactPreview | null>(null);
  const [error, setError] = React.useState('');
  const [fullscreenError, setFullscreenError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [reloadToken, setReloadToken] = React.useState(0);
  const [widthMode, setWidthMode] = React.useState<PreviewWidthMode>('desktop');
  const [customWidth, setCustomWidth] = React.useState(640);
  const [fallbackFullscreen, setFallbackFullscreen] = React.useState(false);
  const [nativeFullscreen, setNativeFullscreen] = React.useState(false);

  React.useEffect(() => {
    const abortController = new AbortController();
    let mounted = true;
    setLoading(true);
    setPreview(null);
    setError('');
    void FileService.getHtmlArtifactPreview(file.id, file.checksum, {
      signal: abortController.signal,
    }).then((nextPreview) => {
      if (!mounted) return;
      setPreview(nextPreview);
    }).catch((reason: unknown) => {
      if (!mounted || (reason instanceof DOMException && reason.name === 'AbortError')) return;
      setPreview(null);
      setError(reason instanceof Error ? reason.message : t('Secure HTML preview is unavailable'));
    }).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [file.checksum, file.id, reloadToken, t]);

  React.useEffect(() => {
    const syncFullscreen = () => {
      const active = Boolean(shellRef.current && document.fullscreenElement === shellRef.current);
      setNativeFullscreen(active);
      if (active) setFallbackFullscreen(false);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  React.useEffect(() => {
    if (!fallbackFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const exitOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFallbackFullscreen(false);
    };
    window.addEventListener('keydown', exitOnEscape);
    return () => {
      window.removeEventListener('keydown', exitOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [fallbackFullscreen]);

  const toggleFullscreen = async () => {
    setFullscreenError('');
    if (fallbackFullscreen) {
      setFallbackFullscreen(false);
      return;
    }
    if (shellRef.current && document.fullscreenElement === shellRef.current) {
      try {
        if (typeof document.exitFullscreen === 'function') await document.exitFullscreen();
        setNativeFullscreen(false);
      } catch (reason) {
        setFullscreenError(reason instanceof Error ? reason.message : t('Fullscreen is unavailable'));
      }
      return;
    }
    const shell = shellRef.current;
    if (!shell) {
      setFullscreenError(t('Fullscreen is unavailable'));
      return;
    }
    try {
      const outcome = await requestViewerFullscreen(shell, {
        fullscreenEnabled: document.fullscreenEnabled,
      });
      if (shellRef.current !== shell) return;
      if (outcome === 'fallback') setFallbackFullscreen(true);
      else setNativeFullscreen(true);
    } catch (reason) {
      setFullscreenError(reason instanceof Error ? reason.message : t('Fullscreen is unavailable'));
    }
  };

  const compact = variant === 'thumbnail';
  const fullscreenActive = fallbackFullscreen || nativeFullscreen;
  const previewDocument = React.useMemo(() => {
    if (!preview) return undefined;
    return widthMode === 'print' ? createPrintPreviewDocument(preview.previewDocument) : preview.previewDocument;
  }, [preview, widthMode]);
  const fullscreenLabel = fullscreenActive ? t('Exit fullscreen') : t('Enter fullscreen');
  const iconControlClassName = 'group relative inline-flex rounded p-1.5 transition-colors hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:cursor-wait disabled:opacity-60 dark:hover:bg-white/10';
  const tooltipClassName = 'pointer-events-none absolute right-0 top-full z-20 mt-1 hidden whitespace-nowrap rounded bg-neutral-900 px-2 py-1 text-[11px] font-normal text-white shadow-lg group-hover:block group-focus-visible:block';
  const viewer = (
    <div
      ref={shellRef}
      data-testid="secure-html-artifact-preview"
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-lg border border-black/10 bg-neutral-100 dark:border-white/10 dark:bg-neutral-950',
        !fullscreenActive && (variant === 'modal' ? 'min-h-[62vh]' : compact ? 'h-full min-h-0 border-0' : 'min-h-80'),
        className,
        fullscreenActive && 'h-[100dvh] w-screen rounded-none',
        fallbackFullscreen && 'fixed inset-0 z-[95]',
      )}
    >
      {!compact && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-black/10 p-2 dark:border-white/10" role="toolbar" aria-label={t('HTML artifact preview controls')}>
          <button type="button" aria-pressed={widthMode === 'desktop'} onClick={() => setWidthMode('desktop')} className="rounded px-2 py-1 text-xs"><Monitor size={14} aria-hidden="true" className="mr-1 inline" />{t('Desktop width')}</button>
          <button type="button" aria-pressed={widthMode === 'mobile'} onClick={() => setWidthMode('mobile')} className="rounded px-2 py-1 text-xs"><Smartphone size={14} aria-hidden="true" className="mr-1 inline" />{t('Mobile width')}</button>
          <button type="button" aria-pressed={widthMode === 'print'} onClick={() => setWidthMode('print')} className="rounded px-2 py-1 text-xs"><Printer size={14} aria-hidden="true" className="mr-1 inline" />{t('Print preview')}</button>
          <button type="button" aria-pressed={widthMode === 'custom'} onClick={() => setWidthMode('custom')} className="rounded px-2 py-1 text-xs">{t('Custom width')}</button>
          <button
            type="button"
            aria-label={t('Reload HTML preview')}
            title={t('Reload HTML preview')}
            disabled={loading}
            onClick={() => setReloadToken((value) => value + 1)}
            className={iconControlClassName}
          >
            <RefreshCw size={14} aria-hidden="true" className={cn(loading && 'animate-spin')} />
            <span role="tooltip" aria-label={t('Reload HTML preview')} className={tooltipClassName}>{t('Reload HTML preview')}</span>
          </button>
          <button
            type="button"
            aria-label={fullscreenLabel}
            title={fullscreenLabel}
            aria-pressed={fullscreenActive}
            onClick={() => void toggleFullscreen()}
            className={iconControlClassName}
          >
            {fullscreenActive ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
            <span role="tooltip" aria-label={fullscreenLabel} className={tooltipClassName}>{fullscreenLabel}</span>
          </button>
        </div>
      )}
      {!compact && widthMode === 'custom' && (
        <label className="flex items-center gap-3 border-b border-black/10 px-3 py-2 text-xs dark:border-white/10">
          <span>{t('Preview width {{width}}px', { width: customWidth })}</span>
          <input type="range" min={320} max={1200} step={10} value={customWidth} onChange={(event) => setCustomWidth(Number(event.currentTarget.value))} className="min-w-32 flex-1" />
        </label>
      )}
      {!compact && fullscreenError && <p className="px-3 py-2 text-sm text-red-600" role="alert">{fullscreenError}</p>}
      <div className="flex min-h-0 flex-1 justify-center overflow-auto bg-neutral-200 p-2 dark:bg-black">
        {loading && <p className="m-auto text-sm text-neutral-500" role="status">{t('Loading secure preview…')}</p>}
        {!loading && error && <p className="m-auto max-w-md text-center text-sm text-red-600" role="alert">{error}. {t('Download remains available.')}</p>}
        {!loading && preview && (
          <div
            className={cn('min-h-0 max-w-full overflow-hidden bg-white shadow-sm transition-[width] duration-200', fullscreenActive && 'h-full')}
            style={{ width: compact ? '100%' : frameWidth(widthMode, customWidth) }}
            data-preview-mode={widthMode}
          >
            <iframe
              key={`${preview.sha256}-${reloadToken}-${widthMode === 'print'}`}
              title={t('Preview of {{fileName}}', { fileName: file.originalName })}
              sandbox=""
              referrerPolicy="no-referrer"
              srcDoc={previewDocument}
              className={cn('block w-full border-0 bg-white', compact || fullscreenActive ? 'h-full min-h-0' : 'h-[58vh] min-h-96')}
            />
          </div>
        )}
      </div>
      {!compact && !loading && preview && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-black/10 px-3 py-2 text-[11px] text-neutral-500 dark:border-white/10">
          <span>{t('{{fileName}} · {{size}} bytes · {{status}}', {
            fileName: preview.filename,
            size: preview.size.toLocaleString(),
            status: t(preview.reviewStatus.replace(/_/g, ' ')),
          })}</span>
        </div>
      )}
    </div>
  );
  return fallbackFullscreen ? createPortal(viewer, document.body) : viewer;
};

export const SecureHtmlArtifactPreview: React.FC<SecureHtmlArtifactPreviewProps> = (props) => {
  const { id, checksum, mimeType, originalName, url } = props.file;
  return <SecureHtmlArtifactViewer key={JSON.stringify([id, checksum, mimeType, originalName, url])} {...props} />;
};
